import React, { useEffect, useRef, useState } from 'react';
import Appearance from 'styles/Appearance.js';
import PageControl from 'views/PageControl.js';
import Request from 'files/Request.js';
import TextField from 'views/TextField.js';
import Views from 'views/Main.js';

const UserLookup = ({ appendContent, containerStyle, isPartnershipRequest = false, metadata = [], onChange, placeholder, utils, value }) => {

    const limit = 10;
    const offset = useRef(0);
    const textTimeout = useRef(null);

    const [loading, setLoading] = useState(false);
    const [paging, setPaging] = useState(null);
    const [results, setResults] = useState([]);
    const [text, setText] = useState('');

    const onClick = async user => {
        try {

            let target = { ...user };

            // clear results and set selected user
            setResults([]);
            setText(user.full_name);

            // determine if metadata was requested
            if(metadata.length > 0) {

                // set loading flag and submit request to server
                setLoading(true);
                const response = await Request.get(utils, '/utils/', {
                    is_partnership_request: isPartnershipRequest,
                    type: 'user_lookup_result_metadata',
                    user_id: user.user_id,
                    ...metadata.reduce((object, key) => ({
                        ...object,
                        [key]: true
                    }), {})
                });
                console.log(response);

                // update target with responses values
                setLoading(false);
                target = { ...target, ...response };
            }

            // notify subscribers of selection change
            if(typeof(onChange) === 'function') {
                onChange(target);
            }
            
        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving the details for this user. ${e.message || 'An unknown error occurred'}`,
                origin: 'user_lookup.on_click'
            });
        }
    }

    const onFieldChange = async input => {
        
        // clear existing timeout if applicable
        if(textTimeout.current) {
            clearTimeout(textTimeout.current);
        }

        // set new text timeout
        textTimeout.current = setTimeout(() => {
            setText(text => {
                if(text !== input) {
                    fetchResults(input);
                }
                return input;
            });
        }, 500);
    }

    const getResults = () => {
        if(loading === false && results.length === 0 && text?.length > 0 && !value) {
            return (
                <div style={{
                    backgroundColor: Appearance.colors.textField(),
                    borderColor: Appearance.colors.softBorder(),
                    borderRadius: 12,
                    borderStyle: 'solid',
                    borderWidth: 1,
                    marginBottom: 12,
                    marginTop: 8,
                    overflow: 'hidden',
                    padding: 0,
                    textAlign: 'left'
                }}>
                    {Views.entry({
                        bottomBorder: false,
                        hideIcon: true,
                        style: { padding: '6px 12px 6px 12px' },
                        subTitle: `Try adjusting your search criteria.`,
                        title: 'No Users Found'
                    })}
                </div>
            )
        }
        if(results.length > 0) {
            return (
                <div style={{
                    backgroundColor: Appearance.colors.textField(),
                    borderColor: Appearance.colors.softBorder(),
                    borderRadius: 12,
                    borderStyle: 'solid',
                    borderWidth: 1,
                    marginBottom: 12,
                    marginTop: 8,
                    overflow: 'hidden',
                    padding: 0,
                    textAlign: 'left'
                }}>
                    {results.map((user, index) => {
                        return (
                            Views.entry({
                                bottomBorder: index !== results.length - 1,
                                hideIcon: true,
                                key: index,
                                onClick: onClick.bind(this, user),
                                style: { padding: '6px 12px 6px 12px' },
                                subTitle: user.email_address,
                                title: user.full_name
                            })
                        )
                    })}
                    {paging && (
                        <PageControl
                        data={paging}
                        limit={limit}
                        offset={offset.current}
                        onClick={next => {
                            offset.current = next;
                            fetchResults(text);
                        }} />
                    )}
                </div>
            )
        }
    }

    const fetchResults = async text => {
        try {

            // no additional logic is required if no text was provided
            if(!text || text.length === 0) {
                setResults([]);
                return;
            }

            // send request to server
            setLoading(true);
            let { paging, users } = await Request.get(utils, '/utils/', {
                is_partnership_request: isPartnershipRequest,
                limit: limit,
                offset: offset.current,
                search_text: text,
                type: 'user_lookup'
            });
            
            // end loading and update local state with results
            setLoading(false);
            setPaging(paging);
            setResults(users);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue completing your search. ${e.message || 'An unknown error occurred'}`,
                origin: 'user_lookup.fetch_results'
            });
        }
    }

    useEffect(() => {
        setText(value?.full_name);
    }, [value]);

    return (
        <div style={{
            position: 'relative',
            width: '100%',
            ...containerStyle
        }}>
            <TextField
            appendContent={appendContent}
            autoCapitalize={'sentences'}
            autoComplete={false}
            autoCorrect={false}
            loading={loading}
            onChange={onFieldChange}
            placeholder={placeholder || 'Search by first and last name...'}
            value={text}
            style={{
                flexGrow: 1,
                ...Appearance.textStyles.title()
            }} />
            {getResults()}
        </div>
    )
}
export default UserLookup;
